import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Trophy, Users } from "lucide-react";
import { useTournamentProgress } from "@/hooks/useTournamentProgress";
import { useUserGroupInfo } from "@/hooks/useUserGroupInfo";
import { MyGroupView } from "./MyGroupView";
import { AllGroupsView } from "./AllGroupsView";
import { EliminationView } from "./EliminationView";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";

interface MyEvent {
  id: string;
  title: string;
  sport: string;
  startDate: string;
  location: string;
  category: string;
  status: "upcoming" | "active" | "finished";
}

interface TournamentProgressCardProps {
  event: MyEvent;
  userRegistered?: boolean;
  viewMode?: 'full' | 'my-group-only';
}

export function TournamentProgressCard({ event, userRegistered = false, viewMode = 'full' }: TournamentProgressCardProps) {
  const [activeTab, setActiveTab] = useState("my-group");
  const { groupInfo, allGroupInfos, loading: groupLoading } = useUserGroupInfo(event.id);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const activeGroupInfo = allGroupInfos.length > 0 ? allGroupInfos[activeGroupIndex] || allGroupInfos[0] : groupInfo;
  const userCategory = activeGroupInfo?.category || null;
  const userSubcategory = activeGroupInfo?.subcategory || null;
  const { phase, progress, loading: progressLoading } = useTournamentProgress(event.id, userCategory, userSubcategory);
  const [scheduleData, setScheduleData] = useState<any>(null);
  const { t, language } = useLanguage();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return date.toLocaleDateString(locale, { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getPhaseLabel = (currentPhase: string) => {
    switch (currentPhase) {
      case 'groups': return t('tournament.groupPhase');
      case 'elimination': return t('tournament.eliminationPhase');
      case 'final': return t('tournament.final');
      case 'finished': return t('tournament.finished');
      default: return t('tournament.groups');
    }
  };

  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'tenis':
      case 'tennis':
      case 'padel':
      case 'pádel':
        return '🎾';
      case 'pickleball':
        return '🏓';
      default:
        return '🏆';
    }
  };

  if (progressLoading || groupLoading) {
    return (
      <Card className="p-6 space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  if (viewMode === 'my-group-only') {
    return (
      <Card className="p-6 space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getSportIcon(event.sport)}</span>
            <h2 className="text-2xl font-bold">{event.title}</h2>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(event.startDate)}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{event.location}</span>
            </div>
            <Badge variant="secondary">{event.category}</Badge>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">{t('tournament.myGroup')}</h3>
          </div>

          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {allGroupInfos.map((info, idx) => (
                <Badge
                  key={idx}
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''} - {info.groupName}
                </Badge>
              ))}
            </div>
          )}
          
          {groupLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : activeGroupInfo ? (
            <MyGroupView groupInfo={activeGroupInfo} />
          ) : (
            <Card className="p-8 text-center space-y-3">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="font-semibold">{t('tournament.preparingTournament')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('tournament.groupsAssignedSoon')}
              </p>
            </Card>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getSportIcon(event.sport)}</span>
              <h2 className="text-2xl font-bold">{event.title}</h2>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(event.startDate)}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
              <Badge variant="secondary">{event.category}</Badge>
            </div>
          </div>
          <Badge variant="default" className="ml-4">
            <Trophy className="w-3 h-3 mr-1" />
            {event.status === 'active' ? t('tournament.active') : event.status === 'finished' ? t('tournament.finished') : t('tournament.upcoming')}
          </Badge>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{getPhaseLabel(phase)}</span>
            <span className="text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className={`text-center ${progress >= 33 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {t('tournament.groups')}
            </div>
            <div className={`text-center ${progress >= 66 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {t('tournament.elimination')}
            </div>
            <div className={`text-center ${progress >= 100 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              {t('tournament.final')}
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="my-group">{t('tournament.myGroup')}</TabsTrigger>
          <TabsTrigger value="all-groups">{t('tournament.allGroups')}</TabsTrigger>
          <TabsTrigger value="elimination">{t('tournament.eliminationPhaseTab')}</TabsTrigger>
        </TabsList>

        <TabsContent value="my-group" className="mt-6">
          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {allGroupInfos.map((info, idx) => (
                <Badge
                  key={idx}
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''} - {info.groupName}
                </Badge>
              ))}
            </div>
          )}
          {groupLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : activeGroupInfo ? (
            <MyGroupView groupInfo={activeGroupInfo} />
          ) : (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                {t('tournament.notAssignedToGroup')}
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all-groups" className="mt-6">
          <AllGroupsView eventId={event.id} scheduleData={scheduleData} />
        </TabsContent>

        <TabsContent value="elimination" className="mt-6">
          <EliminationView eventId={event.id} />
        </TabsContent>
      </Tabs>
    </Card>
  );
}